import React, {useCallback, useEffect, useRef, useState} from "react";
import {Field, useForm} from "react-final-form";
import {
    composeValidators,
    regRequiredValidate,
} from "../../../../containers/RegistrationLoginPage/registrationValidation";
import {
    UserPreferredCourse,
    UserPreferredCourseItem,
} from "../../../../containers/RegistrationLoginPage/types";

import SelectV2 from "../../../HTMLEntities/SelectV2";
import {SINGLE_SELECT} from "../../../HTMLEntities/SelectV2/utils";
import {useDispatch, useSelector} from "react-redux";
import {
    fetchSpecializationData,
    setSpecializationVisibilityAction,
} from "../../../../containers/RegistrationLoginPage/actions";
import {RootStore} from "../../../../configureStore";
import {UseComboboxState} from "downshift";
import {errorHandler} from "../../../../containers/RegistrationLoginPage/errorUtils";
import {isMMPType} from "../../../CTA/response";

interface UserPreferredCourseInterface {
    configuration: UserPreferredCourse;
}

const PreferredCourse: React.FC<UserPreferredCourseInterface> = ({
    configuration,
}: UserPreferredCourseInterface) => {
    const {fieldName, values: dropDownValues, selectedValue, editable} = configuration;

    const getItemsList = useCallback(() => {
        const itemsList: UserPreferredCourseItem[] = [];
        dropDownValues &&
            dropDownValues.map((section) => {
                const {groupOptions} = section;
                groupOptions.map((mainOption: UserPreferredCourseItem) => {
                    itemsList.push(mainOption);
                });
            });
        return itemsList;
    }, [dropDownValues]);

    const [flatItems, setFlatItems] = useState<UserPreferredCourseItem[]>(() => {
        return getItemsList();
    });
    const isMounted = useRef(false);
    useEffect(() => {
        if (isMounted.current) {
            const itemsList: UserPreferredCourseItem[] = getItemsList();
            setFlatItems(itemsList);
        } else {
            isMounted.current = true;
        }
    }, [getItemsList]);

    const dispatch = useDispatch();
    const trackingKey = useSelector((state: RootStore) => state.RegistrationLoginPage.trackingKey);
    const specializationPromiseCancel = useSelector(
        (state: RootStore) => state.RegistrationLoginPage.specializationPromiseCancel,
    );
    const universityId = useSelector(
        (state: RootStore) => state.RegistrationLoginPage.universityId,
    );
    const ctaTrackingData = useSelector(
        (state: RootStore) => state.RegistrationLoginPage.ctaTrackingData,
    );
    const form = useForm();
    const onChangeCallback = useCallback(
        (obj: Partial<UseComboboxState<UserPreferredCourseItem>>) => {
            const registeredFields = form.getRegisteredFields();
            if (registeredFields.includes("preferredSpecializationV1")) {
                form.resetFieldState("preferredSpecializationV1");
                form.change("preferredSpecializationV1", undefined);
            }
            const {selectedItem} = obj;
            if (trackingKey && selectedItem) {
                if (specializationPromiseCancel) {
                    specializationPromiseCancel();
                }
                dispatch(setSpecializationVisibilityAction({loading: true}));
                const params: {
                    trackingId: number;
                    desiredCourseId?: number;
                    categoryId?: number;
                    baseCourseId?: number;
                    streamId?: number;
                    universityId?: number;
                    mmpId?: number;
                    formType?: string;
                } = {
                    trackingId: trackingKey,
                    universityId: universityId,
                    mmpId:
                        ctaTrackingData && isMMPType(ctaTrackingData)
                            ? ctaTrackingData.mmpId
                            : undefined,
                };
                if (selectedItem.type === "baseCourse") {
                    params["baseCourseId"] = selectedItem.id;
                } else if (selectedItem.type === "stream") {
                    params["streamId"] = selectedItem.id;
                } else if (selectedItem.type === "desiredCourse") {
                    params["desiredCourseId"] = selectedItem.id;
                } else if (selectedItem.type === "category") {
                    params["categoryId"] = selectedItem.id;
                }
                params["formType"] = "registration";
                dispatch(fetchSpecializationData(params));
            }
        },
        [form, dispatch, trackingKey, specializationPromiseCancel, universityId, ctaTrackingData],
    );
    return (
        <>
            {flatItems && (
                <Field<UserPreferredCourseItem>
                    name={fieldName}
                    validate={composeValidators(
                        regRequiredValidate("Please select preferred course"),
                    )}
                    {...(selectedValue ? {initialValue: selectedValue} : {})}
                    validateFields={[]}
                >
                    {(props) => {
                        return (
                            <SelectV2
                                placeholder="Preferred Course"
                                type={SINGLE_SELECT}
                                items={flatItems}
                                inputRFF={props.input}
                                withSearch={true}
                                withOptGroup={true}
                                isDisabled={!editable}
                                optGroupItem={dropDownValues ?? []}
                                isFocused={props?.meta?.active ?? false}
                                isTouched={props?.meta?.touched}
                                error={errorHandler(props?.meta)}
                                selectedOption={selectedValue ? [selectedValue] : undefined}
                                onSelectedItemChange={onChangeCallback}
                                isFilled={typeof props.input.value === "object"}
                            />
                        );
                    }}
                </Field>
            )}
        </>
    );
};
export default PreferredCourse;
